import React, { useState, useEffect, useMemo } from "react"; 
import PropTypes from "prop-types";
import {
    useGetTenancyBasicsQuery,
    useGetTenancyByIdQuery,
    useUpdateTenancyMutation,
} from "../../store/services/tenancy.service";
import {
    useGetEmailSimulationSettingsQuery,
    useUploadLandingPageMutation
} from "../../store/services/simulation-service";
import {
    Alert,
    Box,
    Button,
    Grid,
    Snackbar,
    Typography,
    CircularProgress,
} from "@mui/material";
import MainCard from "../../components/cards/MainCard";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import { useThemeContext } from '../../themes/ThemeContext';
import { useSelector } from "react-redux";
import PdfExporter from "../../components/LandingPage/PdfExporter";
import LandingPagePreview from "../../components/LandingPage/LandingPagePreview";
import { publishLandingPage } from "../../components/LandingPage/publishLandingPage";
import { PRESET_LANDING_PAGE } from "../../themes/PresetLandingPage";

function LandingPageSettings(props) {
    const { data: propsData } = props;
    const { tenancyId } = useSelector((state) => state.tenancy);
    const navigate = useNavigate();
    const theme = useTheme();
    const { customColors } = useThemeContext();
    const [isAdmin, setIsAdmin] = useState(false);
    const lightMode = theme.palette.mode === 'light';

    // Query for tenancy data
    const { data: tenancyData, isLoading: tenancyLoading } = useGetTenancyByIdQuery({ tenancyId });
    const { data: tenancyBasics, isLoading: tenancyBasicsLoading } = useGetTenancyBasicsQuery({ tenancyId });
    const {
        data: settingsData,
        isLoading: settingsLoading,
        error: settingsError
    } = useGetEmailSimulationSettingsQuery({ tenancyId });

    const [uploadLandingPage] = useUploadLandingPageMutation();
    const [updateTenancy] = useUpdateTenancyMutation();

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Check for admin role
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
    }, []);

    // For debugging - log what data we have
    useEffect(() => {
        if (tenancyData) {
            console.log("Tenancy Data:", tenancyData);
            console.log("Landing Page URL:", tenancyData.landingPageUrl);
        }
    }, [tenancyData, settingsData]);

    // Handle PDF export success
    const handleExportSuccess = (fileName) => {
        setSnackbarMessage(`Successfully exported PDF as ${fileName}`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    // Handle PDF export error
    const handleExportError = (error) => {
        setSnackbarMessage(`Failed to export PDF: ${error.message || "Unknown error"}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
    };

    // Check for loading state or no data
    if (tenancyLoading || tenancyBasicsLoading || settingsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!tenancyData || !settingsData || !tenancyBasics) {
        return <Typography>No tenancy, settings, or basics data available</Typography>;
    }

    // Only the saved URL is trustworthy. The S3 key is a sanitised form of the tenancy
    // name, so constructing it here would miss any name containing spaces or capitals.
    // When it is absent, PdfExporter publishes the page first via onRecover below.
    const landingPageUrl = tenancyData.landingPageUrl;

    // Generate a safe filename for the PDF
    const tenancyName = tenancyData?.name || 'landing_page';
    const fileName = `${tenancyName.replace(/[^a-z0-9_-]/gi, '_')}_landingPage.pdf`;

    // Construct previewData object - removed useMemo to fix the issue
    const previewData = {
        colors: { primary: settingsData.primary || theme.palette.primary.main },
        logoSize: settingsData.landingPageLogoSize || 120,
        logo: tenancyBasics?.logo,
        showLogo: settingsData.landingPageShowLogo !== undefined ? settingsData.landingPageShowLogo : true,
        showLogoBackground: settingsData.landingPageShowLogoBackground !== undefined ? settingsData.landingPageShowLogoBackground : true,
        companyName: tenancyData.name || 'Your Company',
        buttonText: settingsData.landingPageButtonText || '',
        buttonLink: settingsData.landingPageButtonLink || '',
        headerTitle: settingsData.landingPageHeaderTitle || '',
        mainContent: settingsData.landingPageMainContent || '',
        tipsHeading: settingsData.landingPageTipsHeading || '',
        footer: settingsData.landingPageFooter || '',
        pageTitle: settingsData.landingPageTitle || 'Landing Page',
        backgroundImage: settingsData.landingPageBackgroundImage || '',
        tip1Title: settingsData.landingPageTip1Title || '',
        tip1Content: settingsData.landingPageTip1Content || '',
        tip2Title: settingsData.landingPageTip2Title || '',
        tip2Content: settingsData.landingPageTip2Content || '',
        tip3Title: settingsData.landingPageTip3Title || '',
        tip3Content: settingsData.landingPageTip3Content || '',
        tip4Title: settingsData.landingPageTip4Title || '',
        tip4Content: settingsData.landingPageTip4Content || '',
        tip5Title: settingsData.landingPageTip5Title || '',
        tip5Content: settingsData.landingPageTip5Content || '',
        tip6Title: settingsData.landingPageTip6Title || '',
        tip6Content: settingsData.landingPageTip6Content || '',
        useDefaultsForEmptyStrings: true
    };

    // Publishes the landing page exactly as the editor's Save does, so an export can
    // recover from a tenancy that has never saved one. Returns the fresh S3 URL.
    const handleRecoverLandingPage = () => publishLandingPage({
        templateHTML: PRESET_LANDING_PAGE,
        templateData: previewData,
        tenancyData,
        tenancyId,
        uploadLandingPage,
        updateTenancy
    });

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6} lg={6}>
                                <MainCard
                                    content={true}
                                    boxShadow={true}
                                    border={false}
                                    sx={{
                                        height: '100%',
                                        bgcolor: getCardColor(lightMode),
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        mb: 1
                                    }}></Box>

                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 2,
                                        marginTop: 1
                                    }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="medium"
                                            onClick={() => navigate("/settings/customlandingpage")}
                                            sx={{ px: 3, flex: 1 }}
                                        >
                                            Edit
                                        </Button>

                                        <PdfExporter
                                            url={landingPageUrl}
                                            fileName={fileName}
                                            buttonText="Export to PDF"
                                            companyName={tenancyData?.name || ''}
                                            onRecover={handleRecoverLandingPage}
                                            buttonProps={{
                                                size: "medium",
                                                sx: { px: 3, flex: 1 }
                                            }}
                                            onSuccess={handleExportSuccess}
                                            onError={handleExportError}
                                        />
                                    </Box>
                                    <Box sx={{ marginTop: 2 }}>
                                        {previewData && (
                                            <div className="preview-container" style={{
                                                width: '100%',
                                                height: '485px',
                                                overflow: 'hidden',
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: theme.shape.borderRadius
                                            }}>
                                                <LandingPagePreview
                                                    templateHTML={PRESET_LANDING_PAGE}
                                                    previewData={previewData}
                                                />
                                            </div>
                                        )}
                                    </Box>
                                </MainCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

LandingPageSettings.propTypes = {
    data: PropTypes.object
};

export default LandingPageSettings;